/**
 * @format
 * @module lib/darwin/tests
 */
import { mark, measure } from "lib/performance/user-timing";
import { getProp } from "utils/utils";
import capitalize from "lodash/capitalize";
import logger from "utils/logger";
import { getUrlVars } from "utils/urls";
import { isOnDemand } from "lib/identity/api";

const urlVars = getUrlVars();
const debugTag = "[DARWIN]";

/**
 * @method isDarwinDisabled
 * @description Ensure that Darwin toolkit is on page and if url param is present to disable.
 * The on-demand anonymous/registered users won't see Darwin Tests.
 * @returns {Boolean} - true when disabled
 */
export function isDarwinDisabled() {
  return (
    !window._dw ||
    urlVars.darwin === false ||
    parseInt(urlVars.darwin) === 0 ||
    isOnDemand("enterprise")
  );
}

/**
 * @method declareDarwinPlugin
 * @description Declare the `toTrackVarWithSegments` Darwin plugin.
 * Which determines which Darwin variants are active on the page.
 */
export function declareDarwinPlugin() {
  logger.info(`${debugTag} declareDarwinPlugin()`);
  if (isDarwinDisabled()) {
    return;
  }
  window._dw.push([
    "global",
    "ready",
    function onReady(api) {
      api.$plugins.toTrackVarWithSegments = function trackSegment() {
        let result = [];
        this.toArray().forEach(function handleVariant(variant) {
          // get the original test configuration object
          let test = api.getTestOf(variant);
          // piece together the tracking value with segment string
          if (test.state !== "deregistered") {
            if (test.segments.length !== 0) {
              test.segments.forEach(function handleSegment(segment) {
                result.push([test.name, variant.name || "default", segment].join(":"));
              });
            } else if (test.name !== undefined) {
              // test name is undefined when the test is not live or not
              // part of the test segment
              result.push([test.name, variant.name || "default"].join(":"));
            }
          }
        });
        return result; // Array
      };
    }
  ]);
}

/**
 * @method runDarwinSegments
 * @description Run all Darwin test segments, which determine if the actual test should run.
 * These segments are for variants within the related `before` and `after` arrays.
 */
export function runDarwinTestSegments() {
  runDarwinTests("segments");
}

/**
 * @method runDarwinBeforeTests
 * @description Run all the "before paywall" darwin tests.
 * @param {Object} data - additional data that may be passed to the Darwin method
 * These occur before the paywall is starts processing.
 */
export function runDarwinTestsBefore(data = undefined) {
  runDarwinTests("before", data);
}

/**
 * @method runDarwinAfterTests
 * @description Run all the "after paywall" darwin tests.
 * @param {Object} data - additional data that may be passed to the Darwin method
 * These occur after the paywall is done processing.
 */
export function runDarwinTestsAfter(data = undefined) {
  runDarwinTests("after", data);
}

/**
 * @method runDarwinTests
 * @description Helper method to execute an array of methods.
 * @param {String} property - for marks label and property
 * @param {Object} data - additional data that may be passed to the Darwin method
 */
function runDarwinTests(property, data) {
  if (isDarwinDisabled()) {
    return;
  }
  const label = `runDarwinTests${capitalize(property)}`;
  mark(`${label}_started`);

  // We aggregate the tests via `features/darwin/test/feature.jsp`
  const methods = getProp(window, `tgam.darwin.tests.${property}`, []);
  logger.info(`${debugTag} ${label}:`, methods);

  methods.forEach(methodFn => {
    try {
      methodFn(data);
    } catch (e) {
      logger.error(`${debugTag} ${label} error:`, e);
    }
  });

  mark(`${label}_done`);
  measure(label);
}
